/**
 * Materials API (owner: C1): hand-written types mirroring backend/kb/schemas.py and react-query hooks
 * over /api/kb/materials…, /api/kb/properties… and /api/kb/substances/. Query keys start with
 * "materials". Other features may import this module (it is the feature's public surface), e.g.
 * `searchMaterials` for a material Combobox.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiGet, apiPatch, apiPost } from "@/api/client";
import type { AssetCopy, DecimalString, IsoDate, IsoDateTime, MinorUnits, Paged, Uuid } from "@/api/types";
import { isJobActive, type Job } from "@/features/settings/api";

export type MaterialKind = "molecule" | "natural" | "base" | "solution" | "solvent" | "accord" | "other";
export type NotePosition = "top" | "heart" | "base";
export type TrustTier = "authoritative" | "curated" | "community" | "marketing" | "scraped" | "human";

export const MATERIAL_KINDS: ReadonlyArray<{ value: MaterialKind; label: string }> = [
  { value: "molecule", label: "Molecule" },
  { value: "natural", label: "Natural" },
  { value: "base", label: "Base" },
  { value: "accord", label: "Accord" },
  { value: "solution", label: "Solution" },
  { value: "solvent", label: "Solvent" },
  { value: "other", label: "Other" },
];

export const NOTE_POSITIONS: ReadonlyArray<{ value: NotePosition; label: string }> = [
  { value: "top", label: "Top" },
  { value: "heart", label: "Heart" },
  { value: "base", label: "Base" },
];

export function kindLabel(kind: string): string {
  return MATERIAL_KINDS.find((item) => item.value === kind)?.label ?? kind;
}

export function noteLabel(note: string | null | undefined): string | null {
  if (!note) return null;
  return NOTE_POSITIONS.find((item) => item.value === note)?.label ?? note;
}

export interface SourceRef {
  id: string;
  name: string;
  trust_tier: TrustTier;
  url: string;
}

export interface OdorTermRef {
  id: Uuid;
  kind: "root" | "family" | "subfamily" | "descriptor";
  name_en: string;
  name_fr: string;
  slug: string;
  /** --color-family-<n> swatch (ScenTree family id − 100); null outside the 17 families. */
  swatch: number | null;
}

export interface BestPrice {
  price_per_neat_g_minor: DecimalString;
  currency: string;
  supplier: string;
  variant_title: string;
  pack_g: DecimalString | null;
  pack_qty?: DecimalString | null;
  pack_unit?: string | null;
  dilution_fraction: DecimalString | null;
  in_stock: boolean | null;
  /** A volume pack weighed at 1 g/mL, the material's density not being known. */
  density_assumed?: boolean;
}

export interface MaterialListItem {
  id: Uuid;
  name: string;
  kind: MaterialKind;
  trade_name: string;
  grade: string;
  short_code: string | null;
  note_position: NotePosition | null;
  is_private: boolean;
  substance_id: Uuid | null;
  substance_name: string | null;
  cas: string | null;
  cas_relation: string | null;
  odor_path: OdorTermRef[];
  owned: boolean;
  lot_count: number;
  stock_neat_g: DecimalString | null;
  has_offers: boolean;
  best_price: BestPrice | null;
}

export type MaterialSort = "name" | "-name" | "kind" | "note_position" | "relevance";

export interface MaterialListParams {
  q?: string;
  kind?: MaterialKind;
  note_position?: NotePosition;
  odor_term?: Uuid;
  owned?: boolean;
  has_offers?: boolean;
  scope?: "global" | "private";
  sort?: MaterialSort;
  limit?: number;
  offset?: number;
}

export interface NamedRef {
  id: Uuid;
  name: string;
}

export interface MaterialCore {
  id: Uuid;
  name: string;
  kind: MaterialKind;
  trade_name: string;
  grade: string;
  short_code: string | null;
  note_position: NotePosition | null;
  odor_summary: string;
  notes: string;
  default_density: DecimalString | null;
  is_solvent: boolean;
  is_natural: boolean;
  animal_derived: boolean;
  vegan: boolean | null;
  shelf_life_months: number | null;
  storage_advice: string;
  is_private: boolean;
  /** Fields PATCH accepts: every field on private materials, annotations on shared ones. */
  editable_fields: string[];
  source: SourceRef | null;
  manufacturer: NamedRef | null;
  merged_into: NamedRef | null;
  /** The material this is a variant of: the same natural from one origin (`origin`). */
  variant_of: NamedRef | null;
  origin: string;
  created_at: IsoDateTime;
  updated_at: IsoDateTime;
}

export interface MaterialVariant {
  id: Uuid;
  name: string;
  origin: string;
}

export interface SubstanceInfo {
  id: Uuid;
  preferred_name: string;
  kind: string;
  formula: string;
  mw: DecimalString | null;
  inchikey: string | null;
  smiles: string;
  taxon_name: string;
  plant_part: string;
  process: string;
  post_treatment: string;
  chemotype: string;
  origin: string;
  parent: NamedRef | null;
  review_status: string;
  is_private: boolean;
}

export interface IdentifierRow {
  scheme: string;
  value: string;
  relations: string[];
  sources: SourceRef[];
  primary: boolean;
  erroneous: boolean;
}

export interface NameRow {
  text: string;
  kinds: string[];
  languages: string[];
  sources: SourceRef[];
  owners: string[];
  subjects: string[];
}

export interface ManufacturerProduct {
  id: Uuid;
  organization: string;
  product_name: string;
  product_code: string;
  url: string;
  naturality: string;
  purity: string;
  certifications: unknown[];
  source: SourceRef | null;
}

export interface OdorPathRow {
  id: Uuid;
  rank: number;
  source: SourceRef | null;
  terms: OdorTermRef[];
}

export type PropertyStatus = "selected" | "candidate" | "rejected_outlier" | "superseded";

export interface PropertyCandidate {
  id: Uuid;
  property: string;
  value_num: DecimalString | null;
  value_low: DecimalString | null;
  value_high: DecimalString | null;
  unit: string;
  value_text: string;
  conditions: Record<string, unknown>;
  method: string;
  raw_value: string;
  source: SourceRef | null;
  source_url: string;
  /** Our copy of the source when it is a document (a supplier's PDF), when one was saved. */
  source_copy?: AssetCopy | null;
  retrieved_at: IsoDateTime | null;
  confidence: DecimalString | null;
  status: PropertyStatus;
  subject: "material" | "substance";
  reference_only: boolean;
}

export type PropertyCategory = "odor" | "physical" | "identity" | "commercial" | "reference" | "other";

export interface PropertyGroup {
  property: string;
  label: string;
  category: PropertyCategory;
  /** The candidate the material uses: a selection, else the best by trust tier, confidence and recency. */
  effective_id: Uuid | null;
  has_selection: boolean;
  candidates: PropertyCandidate[];
}

export interface MaterialOverview {
  volatility: PropertyCandidate | null;
  detection_threshold: PropertyCandidate | null;
  price_band: PropertyCandidate | null;
  max_use: PropertyCandidate | null;
  substantivity: PropertyCandidate | null;
  vapor_pressure: PropertyCandidate | null;
  uses: PropertyCandidate | null;
  appearance: PropertyCandidate | null;
  odor_descriptions: PropertyCandidate[];
}

export interface CompositionEntry {
  component_id: Uuid;
  component_name: string;
  cas: string | null;
  min_fraction: DecimalString | null;
  typical_fraction: DecimalString | null;
  max_fraction: DecimalString | null;
  label_as_published: string;
}

export interface CompositionVersion {
  id: Uuid;
  basis: string;
  kind: "typical" | "range" | "lot_actual" | "certificate";
  source: SourceRef | null;
  subject: "material" | "substance";
  lot_id: Uuid | null;
  valid_from: IsoDate | null;
  notes: string;
  created_at: IsoDateTime;
  entries: CompositionEntry[];
}

export interface DatasetRef {
  id: Uuid;
  title: string;
  version_label: string;
  kind: string;
  status: string;
  publisher: string;
  source_url: string;
}

export interface IfraLimit {
  category: string;
  limit_type: "max_pct_finished" | "prohibited" | "specification" | "labelling_threshold" | "max_pct_product_type";
  value_pct: DecimalString | null;
  applies_to: string;
}

export interface IfraStandard {
  instrument_key: string;
  name: string;
  standard_type: string;
  amendment: unknown;
  group_key: string;
  matched_cas: string[];
  intrinsic_property: string;
  specification: string;
  notes: string;
  contributions_from_other_sources: string;
  deadline_new_creations: string;
  deadline_existing_creations: string;
  applies_to: string;
  valid_from: IsoDate | null;
  dataset_id: Uuid;
  limits: IfraLimit[];
}

export interface AllergenEntry {
  instrument_key: string;
  name: string;
  label_name: string;
  is_ncs: boolean;
  entry_status: string;
  matched_cas: string[];
  valid_from: IsoDate | null;
  leave_on_threshold_pct: DecimalString | null;
  rinse_off_threshold_pct: DecimalString | null;
  dataset_id: Uuid;
}

export interface CapEntry {
  instrument_key: string;
  name: string;
  category: string;
  product_type: string;
  value_pct: DecimalString | null;
  valid_from: IsoDate | null;
  regulation: string;
  matched_cas: string[];
  dataset_id: Uuid;
}

export interface HazardRow {
  jurisdiction: string;
  hazard_class: string;
  category: string;
  h_code: string;
  signal_word: string;
  pictograms: unknown[];
  source: SourceRef | null;
  source_url: string;
}

export interface Regulatory {
  cas_numbers: string[];
  is_natural: boolean;
  ifra: IfraStandard[];
  allergens: AllergenEntry[];
  caps: CapEntry[];
  hazards: HazardRow[];
  /** Datasets that matched at least one rule. */
  datasets: DatasetRef[];
  /** Every in-force dataset that was searched. */
  datasets_checked: DatasetRef[];
}

export interface Offer {
  variant_id: Uuid;
  product_id: Uuid;
  supplier: { id: Uuid; slug: string; name: string };
  product_title: string;
  variant_title: string;
  url: string;
  pack_qty: DecimalString | null;
  pack_unit: string | null;
  pack_g: DecimalString | null;
  dilution_fraction: DecimalString | null;
  solvent_text: string;
  /** In the workspace currency, converted at the day's rate when the store lists another. */
  price_minor: MinorUnits | null;
  currency: string;
  /** As the store lists it. */
  listed_price_minor: MinorUnits | null;
  listed_currency: string;
  /** How the price was converted, or why there is no price per gram. */
  notes: string[];
  market: string;
  /** False: the latest price is cached from another market. */
  in_workspace_market: boolean;
  in_stock: boolean | null;
  observed_at: IsoDateTime | null;
  price_per_neat_g_minor: DecimalString | null;
  match: { method: string; review_status: string; confidence: DecimalString | null };
  /** On a material's page, the variant this listing is of (its origin). */
  material_variant?: MaterialVariant | null;
  /** A volume pack weighed at 1 g/mL, the material's density not being known. */
  density_assumed?: boolean;
}

export interface MaterialLot {
  id: Uuid;
  code: string;
  display_code: string;
  /** `on_order`: ordered, not received yet (no stock). */
  status: "on_order" | "active" | "empty" | "disposed";
  origin: string;
  active_fraction: DecimalString;
  carrier_name: string | null;
  balance_g: DecimalString;
  neat_g: DecimalString;
  labelled_qty_g: DecimalString | null;
  location: { id: Uuid; code: string; name: string } | null;
  supplier: string | null;
  supplier_lot_number: string;
  received_at: IsoDateTime | null;
  ordered_at: IsoDate | null;
  expires_at: IsoDate | null;
  underweight: boolean;
}

export interface UsageStat {
  id: Uuid;
  material_id: Uuid;
  material_name: string;
  material_kind: string;
  source_kind: "demo_formulas" | "patents" | "workspace";
  is_workspace: boolean;
  n_formulas: number;
  median_pct: DecimalString | null;
  p25_pct: DecimalString | null;
  p75_pct: DecimalString | null;
  max_pct: DecimalString | null;
  computed_at: IsoDateTime;
}

export interface MaterialDetail {
  material: MaterialCore;
  substance: SubstanceInfo | null;
  /** The same material from other origins; their listings and lots count as this material's. */
  variants: MaterialVariant[];
  identifiers: IdentifierRow[];
  names: NameRow[];
  manufacturer_products: ManufacturerProduct[];
  odor_paths: OdorPathRow[];
  overview: MaterialOverview;
  properties: PropertyGroup[];
  compositions: CompositionVersion[];
  regulatory: Regulatory;
  offers: Offer[];
  lots: MaterialLot[];
  stock: {
    lot_count: number;
    active_lot_count: number;
    neat_g: DecimalString | null;
    /** Lots still on order and the neat grams they'll bring (not part of `neat_g`). */
    on_order_lot_count: number;
    on_order_neat_g: DecimalString | null;
  };
  usage: UsageStat[];
  evaluation_count: number;
  open_review_count: number;
}

export interface PricePoint {
  observed_at: IsoDateTime;
  /** In the history's currency, at today's rate. */
  price_minor: MinorUnits;
  currency: string;
  listed_price_minor: MinorUnits;
  listed_currency: string;
  compare_at_minor: MinorUnits | null;
  in_stock: boolean;
  price_per_neat_g_minor: DecimalString | null;
  /** Per neat gram, or per neat mL for a volume pack without a density (the series' `unit`). */
  price_per_unit_minor: DecimalString | null;
}

export interface PriceSeries {
  variant_id: Uuid;
  supplier: string;
  supplier_slug: string;
  product_title: string;
  variant_title: string;
  pack_qty: DecimalString | null;
  pack_unit: string | null;
  pack_g: DecimalString | null;
  /** Grams in the pack as sold; volume packs through the material's density. */
  pack_grams: DecimalString | null;
  dilution_fraction: DecimalString | null;
  unit: "g" | "mL" | null;
  /** When a sync last saw the offer in this market: the last point's price holds until then. */
  last_seen_at: IsoDateTime | null;
  points: PricePoint[];
}

export interface PriceHistory {
  market: string;
  /** The workspace currency: every price is converted to it at today's rate. */
  currency: string;
  /** How listed prices were converted. */
  notes: string[];
  series: PriceSeries[];
}

export interface SubstanceListItem {
  id: Uuid;
  preferred_name: string;
  kind: string;
  cas: string | null;
  taxon_name: string;
  material_count: number;
  is_private: boolean;
}

export interface MaterialCreateInput {
  name: string;
  kind?: MaterialKind | null;
  cas?: string | null;
  substance_id?: Uuid | null;
  short_code?: string | null;
  note_position?: NotePosition | null;
  trade_name?: string | null;
  odor_summary?: string | null;
  synonyms?: string[];
}

export type MaterialUpdateInput = Partial<{
  name: string;
  kind: MaterialKind;
  short_code: string | null;
  note_position: NotePosition | null;
  odor_summary: string;
  notes: string;
  trade_name: string;
  grade: string;
  default_density: DecimalString | null;
  is_solvent: boolean;
  is_natural: boolean;
  animal_derived: boolean;
  vegan: boolean | null;
  shelf_life_months: number | null;
  storage_advice: string;
}>;

export const materialsKeys = {
  all: ["materials"] as const,
  list: (params: MaterialListParams) => ["materials", "list", params] as const,
  detail: (id: Uuid) => ["materials", "detail", id] as const,
  priceHistory: (id: Uuid, market?: string) => ["materials", "price-history", id, market ?? null] as const,
  substances: (q: string) => ["materials", "substances", q] as const,
};

export function fetchMaterials(params: MaterialListParams, signal?: AbortSignal): Promise<Paged<MaterialListItem>> {
  return apiGet<Paged<MaterialListItem>>("/kb/materials/", { params: { ...params }, signal });
}

/** Material search for comboboxes elsewhere in the app (name, synonym, CAS or short code). */
export async function searchMaterials(query: string, signal?: AbortSignal, limit = 20): Promise<MaterialListItem[]> {
  const page = await fetchMaterials({ q: query, limit }, signal);
  return page.items;
}

export async function searchSubstances(query: string, signal?: AbortSignal): Promise<SubstanceListItem[]> {
  const page = await apiGet<Paged<SubstanceListItem>>("/kb/substances/", { params: { q: query, limit: 20 }, signal });
  return page.items;
}

export function useMaterials(params: MaterialListParams) {
  return useQuery({
    queryKey: materialsKeys.list(params),
    queryFn: ({ signal }) => fetchMaterials(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useMaterial(id: Uuid | undefined) {
  return useQuery({
    queryKey: materialsKeys.detail(id ?? ""),
    queryFn: ({ signal }) => apiGet<MaterialDetail>(`/kb/materials/${id}`, { signal }),
    enabled: Boolean(id),
  });
}

export function usePriceHistory(id: Uuid, market?: string, enabled = true) {
  return useQuery({
    queryKey: materialsKeys.priceHistory(id, market),
    queryFn: ({ signal }) => apiGet<PriceHistory>(`/kb/materials/${id}/price-history`, { params: { market }, signal }),
    enabled,
  });
}

/** `POST /api/suppliers/offers/refresh`: the job checking the listings whose last check is stale. */
export interface OfferRefresh {
  /** Null when every listing is current, or no worker is running. */
  job_id: Uuid | null;
  suppliers: Array<{ id: Uuid; slug: string; name: string }>;
  listings: number;
}

// A refresh still running after this long is left to finish without the page waiting on it.
const OFFER_REFRESH_WAIT_MS = 60_000;

/**
 * When a material page opens, asks for a check of the current prices of its listings whose last check
 * is stale, then follows the job, reloading the material and its price history whenever another
 * supplier's prices land. `checking` names the suppliers being asked while the job runs.
 */
export function useOfferRefresh(materialId: Uuid | undefined) {
  const queryClient = useQueryClient();
  const request = useMutation({
    mutationFn: (id: Uuid) => apiPost<OfferRefresh>("/suppliers/offers/refresh", { material_id: id }),
  });
  const { mutate } = request;
  useEffect(() => {
    if (materialId) mutate(materialId);
  }, [materialId, mutate]);

  const jobId = request.data?.job_id ?? null;
  const [abandonedJobId, setAbandonedJobId] = useState<Uuid | null>(null);
  useEffect(() => {
    if (!jobId) return;
    const timer = window.setTimeout(() => setAbandonedJobId(jobId), OFFER_REFRESH_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [jobId]);
  const abandoned = jobId !== null && abandonedJobId === jobId;

  const job = useQuery({
    queryKey: ["jobs", "detail", jobId],
    queryFn: ({ signal }) => apiGet<Job>(`/jobs/${jobId}`, { signal }),
    enabled: Boolean(jobId) && !abandoned,
    refetchInterval: (query) =>
      query.state.status === "error" || (query.state.data && !isJobActive(query.state.data)) ? false : 1000,
  });

  // Suppliers whose prices have landed: the job's progress while it runs, all of them once it ends.
  const landed = job.data ? (isJobActive(job.data) ? (job.data.progress.done ?? 0) : Infinity) : 0;
  useEffect(() => {
    if (!materialId || landed === 0) return;
    void queryClient.invalidateQueries({ queryKey: materialsKeys.detail(materialId) });
    void queryClient.invalidateQueries({ queryKey: ["materials", "price-history", materialId] });
  }, [landed, materialId, queryClient]);

  const running = Boolean(jobId) && !abandoned && !job.isError && (!job.data || isJobActive(job.data));
  return { checking: running ? (request.data?.suppliers ?? []) : [] };
}

export function useCreateMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MaterialCreateInput) => apiPost<MaterialDetail>("/kb/materials/", body),
    onSuccess: (detail) => queryClient.setQueryData(materialsKeys.detail(detail.material.id), detail),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["materials", "list"] }),
  });
}

export function useUpdateMaterial(id: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: MaterialUpdateInput) => apiPatch<MaterialDetail>(`/kb/materials/${id}`, body),
    onSuccess: (detail) => queryClient.setQueryData(materialsKeys.detail(id), detail),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["materials", "list"] }),
  });
}

export function useSetPropertyStatus(materialId: Uuid) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      valueId,
      status,
      note,
    }: {
      valueId: Uuid;
      status: "selected" | "candidate" | "rejected_outlier";
      note?: string;
    }) =>
      apiPost<{ changed: PropertyCandidate[] }>(`/kb/properties/${valueId}/status`, {
        status,
        material_id: materialId,
        note: note ?? "",
      }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: materialsKeys.detail(materialId) }),
  });
}

export { UsageRangeBar, usageSummary, type UsageRange } from "./UsageRangeBar";
